import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { BookOpen, UserRound } from 'lucide-react';
import { brandingStyle, resolveBranding } from '../branding';
import { LocaleSwitch } from '../components/locale-switch';
import { UiIcon } from '../components/ui-icon';
import { useI18n } from '../i18n/i18n-provider';
import { useSession } from '../session/session-provider';
import { OrganizationBrandingProvider, useOrganizationBranding } from '../organization-branding-provider';
import { BrandingWordmark, StandardBrandingWordmark } from '../components/studio-events-view';
import { DefaultOrganizationWordmark, organizationWorkspaceStyle } from '../components/organization-branding-view';
import { EventUiProvider } from '../components/event-ui-provider';
import '../components/workspace-template.css';
import { AdminWorkspaceNavigation } from './admin-layout';
import { PwaStatus } from '../pwa/pwa-status';

export function AuthenticatedLayout() {
  return <OrganizationBrandingProvider><AuthenticatedWorkspace /></OrganizationBrandingProvider>;
}

function AuthenticatedWorkspace() {
  const { t } = useI18n();
  const {
    canAccessAdminWorkspace,
    canAccessAttendeeWorkspace,
    isPlatformAdmin,
    signOut,
    tenant,
    user,
    managedMembership,
    account,
  } = useSession();
  const location = useLocation();
  const branding = resolveBranding(location.pathname.startsWith('/platform') ? undefined : tenant);
  const { active, logoUrl, status: brandingStatus } = useOrganizationBranding();
  const tenantBranding = tenant && !location.pathname.startsWith('/platform') ? active : null;
  const defaultOrganizationLogo = Boolean(tenant && !location.pathname.startsWith('/platform') && brandingStatus === 'success' && !active);
  const template = tenantBranding?.templateId ?? 'standard';
  const editorial = template === 'editorial' && location.pathname.startsWith('/attendee');
  const studio = template !== 'standard' && location.pathname.startsWith('/attendee');
  const workingIdentity = tenantBranding && template !== 'standard';
  const multipleOrganizations = (account?.organizations.length ?? 0) > 1;
  const showOrganizationAccount = managedMembership && (multipleOrganizations || Boolean(account?.canCreateOrganization));
  const organizationAccountLabel = multipleOrganizations ? t('organization.title') : t('organization.create');
  const theme = { ...brandingStyle(branding), ...(tenantBranding ? organizationWorkspaceStyle(tenantBranding) : {}) };

  return (
    <EventUiProvider tenantScope style={theme}>
    <div
      className={`app-shell app-shell--workspace${studio ? ' app-shell--studio' : ''}${editorial ? ' app-shell--editorial' : ''}${workingIdentity ? ` app-shell--tenant-template app-shell--template-${template}` : ''}`}
      data-brand={branding.key}
      data-organization-template={tenantBranding?.templateId}
      style={theme}
    >
      <PwaStatus />
      {studio && tenantBranding ? <header className={`studio-header${editorial ? ' editorial-header' : ''}${template === 'focus' ? ' focus-header' : ''}`}>
        <Link to="/attendee/events" className="studio-wordmark" aria-label={tenantBranding.displayName}>
          <BrandingWordmark branding={tenantBranding} logoUrl={logoUrl} />
        </Link>
        <nav className="studio-links" aria-label={t('nav.attendee')}>
          <NavLink to="/attendee/events">{t('nav.events')}</NavLink>
          <NavLink to="/attendee/enrollments">{t('nav.enrollments')}</NavLink>
          <NavLink to="/attendee/tasks">{t('nav.tasks')}</NavLink>
        </nav>
        <LocaleSwitch />
        <Link to="/help" className="studio-help-link"><BookOpen size={18} aria-hidden="true" />{t('help.nav')}</Link>
        <details className="studio-account">
          <summary className="studio-avatar" aria-label={user?.displayName ?? t('nav.workspaces')}><UserRound size={18} aria-hidden="true" /></summary>
          <div className="studio-account-menu">
            <strong>{user?.displayName}</strong>
            <small>{tenant?.displayName}</small>
            <small>{user?.roles.map((role) => t(`role.${role}`)).join(' • ')}</small>
            <Link to="/attendee">{t('nav.overview')}</Link>
            {showOrganizationAccount && <Link to="/account">{organizationAccountLabel}</Link>}
            {canAccessAdminWorkspace && <Link to="/admin">{t('nav.admin')}</Link>}
            {isPlatformAdmin && <Link to="/platform">{t('nav.platform')}</Link>}
            <button type="button" onClick={signOut}>{t('auth.signOut')}</button>
          </div>
        </details>
      </header> : <header className={`app-header app-header--workspace${tenantBranding || defaultOrganizationLogo ? ' standard-header' : ''}${workingIdentity ? ` workspace-template-header workspace-template-header--${template}` : ''}`}>
        <Link to="/" className="app-brand" aria-label={tenantBranding?.displayName ?? (defaultOrganizationLogo ? tenant?.displayName : t('app.name'))}>
          {tenantBranding ? <StandardBrandingWordmark branding={tenantBranding} logoUrl={logoUrl} /> : defaultOrganizationLogo && tenant ? <DefaultOrganizationWordmark organizationName={tenant.displayName} /> : <><span className="app-brand__mark"><img src="/pwa/event-app-192-v1.png" width="44" height="44" alt="" /></span>
          <span className="app-brand__lockup">
            <span className="app-brand__name">{t('app.name')}</span>
            <span className="app-brand__meta">
              {location.pathname.startsWith('/platform') ? t('nav.platform') : tenant?.displayName ?? t('tenant.default')}
            </span>
          </span></>}
        </Link>
        {workingIdentity && template === 'editorial' && location.pathname.startsWith('/admin') && <AdminWorkspaceNavigation masthead />}
        <nav className="app-nav" aria-label={t('nav.workspaces')}>
          {showOrganizationAccount && <NavLink to="/account" className="app-nav__link">{organizationAccountLabel}</NavLink>}
          {canAccessAttendeeWorkspace && (
            <NavLink to="/attendee" className="app-nav__link">
              <span className="app-nav__link-label">
                <span className="app-nav__link-icon">
                  <UiIcon name="calendar" className="feature-card__icon-svg" />
                </span>
                {t('nav.attendee')}
              </span>
            </NavLink>
          )}
          {canAccessAdminWorkspace && (
            <NavLink to="/admin" className="app-nav__link">
              <span className="app-nav__link-label">
                <span className="app-nav__link-icon">
                  <UiIcon name="scanner" className="feature-card__icon-svg" />
                </span>
                {t('nav.admin')}
              </span>
            </NavLink>
          )}
          {isPlatformAdmin && (
            <NavLink to="/platform" className="app-nav__link">
              <span className="app-nav__link-label">
                <span className="app-nav__link-icon">
                  <UiIcon name="platform" className="feature-card__icon-svg" />
                </span>
                {t('nav.platform')}
              </span>
            </NavLink>
          )}
        </nav>
        <div className="app-header__tools">
          {tenant && (
            <span className="workspace-pill" aria-label={t('tenant.current')}>
              <UiIcon name="tenant" className="workspace-pill__icon" />
              {tenant.displayName}
            </span>
          )}
          {user && (
            <div className="workspace-user">
              <strong>{user.displayName}</strong>
              <span>{user.roles.map((role) => t(`role.${role}`)).join(' • ')}</span>
            </div>
          )}
          <LocaleSwitch />
          <NavLink to="/help" className="cta cta--secondary cta--compact"><BookOpen size={18} aria-hidden="true" />{t('help.nav')}</NavLink>
          <button
            type="button"
            className="cta cta--secondary cta--compact"
            onClick={signOut}
          >
            {t('auth.signOut')}
          </button>
        </div>
      </header>}
      <main className="app-main">
        <Outlet />
      </main>
      <footer className="app-footer">
        <a href="https://vcode.zeleznalady.cz" target="_blank" rel="noopener noreferrer">{t('workspace.poweredBy')}</a>
      </footer>
    </div>
    </EventUiProvider>
  );
}
